import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "lib/router";
import { Popover } from "radix-ui";
import { Bell, Info, Menu } from "lucide-react";
import moment from "moment";
import { ErrorToaster } from "components/Toaster";
import SystemServices from "services/System";
import { cn } from "ui";

const parseData = (raw) => {
  try {
    return JSON.parse(raw) || {};
  } catch {
    return {};
  }
};

/** Top bar of the app shell: sidebar toggle (mobile) and the notifications menu. */
function Header({ onMenu }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);

  const getNotifications = async () => {
    try {
      const { data } = await SystemServices.getNotifications();
      setNotificationCount(data?.unreadNotifications);
      setNotifications(data?.notifications?.rows);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const notificationSeen = async (id, route) => {
    try {
      await SystemServices.notificationSeen({ notification_id: id });
      setOpen(false);
      navigate(route ? route : "/dashboard");
    } catch (error) {
      ErrorToaster(error);
    }
  };

  useEffect(() => {
    getNotifications();
  }, [location?.pathname]);

  return (
    <header className="sticky top-0 z-[1100] flex h-14 shrink-0 items-center justify-between gap-3 border-b border-line bg-panel/85 px-4 backdrop-blur md:px-6">
      <button type="button" onClick={onMenu} aria-label="Open menu" className="grid size-9 place-items-center rounded-lg text-muted hover:bg-black/[0.045] hover:text-fg md:hidden">
        <Menu className="size-5" />
      </button>
      <div className="flex-1" />
      <Popover.Root open={open} onOpenChange={setOpen}>
        <Popover.Trigger asChild>
          <button
            type="button"
            aria-label={`Notifications${notificationCount ? `, ${notificationCount} unread` : ""}`}
            className="relative grid size-9 place-items-center rounded-lg border border-line bg-panel text-muted shadow-card hover:bg-[#f7f7f8] hover:text-fg"
          >
            <Bell className="size-[18px]" />
            {notificationCount > 0 && (
              <span className="absolute -top-1.5 -right-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-danger px-1 text-[10px] font-semibold text-white tabular-nums">
                {notificationCount > 99 ? "99+" : notificationCount}
              </span>
            )}
          </button>
        </Popover.Trigger>
        <Popover.Portal>
          <Popover.Content align="end" sideOffset={8} className="z-[1400] flex max-h-[480px] w-[380px] max-w-[calc(100vw-24px)] flex-col rounded-xl border border-line bg-panel shadow-pop">
            <div className="flex items-center justify-between border-b border-line px-4 py-3">
              <span className="text-[14px] font-semibold text-fg">Notifications</span>
              {notificationCount > 0 && <span className="text-[12px] text-muted">{notificationCount} unread</span>}
            </div>
            <ul className="min-h-0 flex-1 divide-y divide-[#efeff3] overflow-y-auto">
              {notifications?.length ? (
                notifications.map((item) => {
                  const link = parseData(item?.data);
                  return (
                    <li key={item.id}>
                      <button type="button" onClick={() => notificationSeen(item?.id, link?.route)} className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-black/[0.025]">
                        <span className="grid size-8 shrink-0 place-items-center rounded-full bg-accent-soft text-accent">
                          <Info className="size-4" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className={cn("block text-[13px] whitespace-pre-line text-fg", !item?.is_read && "font-semibold")}>{item.content}</span>
                          <span className="mt-0.5 block text-[11.5px] text-faint">{moment(item?.created_at).format("DD/MM/YYYY, hh:mm A")}</span>
                        </span>
                        {!item?.is_read && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" aria-label="Unread" />}
                      </button>
                    </li>
                  );
                })
              ) : (
                <li className="px-4 py-10 text-center text-[13px] text-muted">You're all caught up.</li>
              )}
            </ul>
          </Popover.Content>
        </Popover.Portal>
      </Popover.Root>
    </header>
  );
}

export default Header;
